import { useMemo } from 'react';
import { Location, useLocation, useSearchParams } from 'react-router-dom';
import {
  NavigationCustomizationDataDtoResponse,
  NavigationMenuItemDto,
  NavigationPageItemDto,
  PageDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';

import {
  createNavigationCustomization,
  fetchNavigationCustomization,
} from '../api/portalGateway/navigationCustomization';
import { NO_ROLE_ID } from '../api/portalGatewayClient';
import { useStore } from '../store';
import { getDetailsFromSearchParams } from '../utils/getDetailsFromSearchParams';
import { prependRoleId } from '../utils/prependRoleId';
import { pageWithRoleLink } from '../utils/urlPath';

// create a new dynamic navigation item if not already present
export const useCreateDynamicNavItem = (
  navigationData?: NavigationMenuItemDto | NavigationPageItemDto,
  pageData?: PageDto
) => {
  const roleId = useStore((state) => state.activeUserRole?.id);
  const queryClient = useQueryClient();
  const createMutation = useCreateNavigationCustomizationMutation();
  const [urlSearchParams] = useSearchParams();
  const { state }: Location<{ originHref?: string } | null> = useLocation();

  const navigationCustomizationData:
    | NavigationCustomizationDataDtoResponse
    | undefined = queryClient.getQueryData(
    prependRoleId(['navigationCustomization'])
  );

  const itemId = useMemo(() => {
    let item = null;
    let itemId = null;
    let href: string | null = null;
    const {
      detailsDiscriminator: urlParamsDetailsDiscriminator,
      detailsValue: urlParamsDetailValue,
    } = getDetailsFromSearchParams(urlSearchParams);
    if (
      navigationData != null &&
      navigationCustomizationData != null &&
      navigationData.type === 'navigation-page-item' &&
      pageData?.type === 'detailPage' &&
      // do not create items for any url missing the details discriminator or the details value
      urlParamsDetailsDiscriminator != null &&
      urlParamsDetailValue != null
    ) {
      href = pageWithRoleLink(roleId ?? NO_ROLE_ID, {
        pageType: 'detailPage',
        slug: navigationData.completeSlug,
      });
      if (urlSearchParams.toString().length > 0) {
        href = `${href}?${urlSearchParams.toString()}`;
      }
      const rootSlug = navigationData.completeSlug.split('/')[0];
      // find the root item for the current detailpage tree
      // needs a matching root page id, matching details discriminator & value
      item = navigationCustomizationData?.data.dynamicNavigationItems.find(
        (di) => {
          const dynamicItemUrlSearch = new URLSearchParams(
            di.href.split('?').pop()
          );

          const { detailsDiscriminator, detailsValue } =
            getDetailsFromSearchParams(dynamicItemUrlSearch);

          return (
            di.href.includes(rootSlug) &&
            detailsDiscriminator === urlParamsDetailsDiscriminator &&
            detailsValue === urlParamsDetailValue
          );
        }
      );
      // create an id only if an item is created
      itemId = item != null ? item.id : null;
      if (item == null && href != null && !createMutation.isPending) {
        itemId = uuid();
        createMutation.mutate({
          data: {
            id: itemId,
            label: urlSearchParams.get('label') || navigationData.label,
            href,
            icon: navigationData.icon ?? 'draft',
            originHref: state?.originHref,
          },
        });
      }
    }
    return itemId;
  }, [
    createMutation,
    navigationData,
    pageData,
    roleId,
    navigationCustomizationData,
    urlSearchParams,
    state,
  ]);

  return itemId ?? null;
};

function useCreateNavigationCustomizationMutation() {
  const options = queryOptions({
    queryKey: prependRoleId(['navigationCustomization']),
    queryFn: fetchNavigationCustomization,
  });
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createNavigationCustomization,
    onMutate: async (newNavItem) => {
      await queryClient.cancelQueries(options);
      const previousNavigationCustomization = queryClient.getQueryData(
        options.queryKey
      );

      // optimistically update to the new value
      queryClient.setQueryData(options.queryKey, (old) =>
        old
          ? {
              ...old,
              data: {
                dynamicNavigationItems: [
                  ...old.data.dynamicNavigationItems,
                  newNavItem.data,
                ],
              },
            }
          : undefined
      );

      return { previousNavigationCustomization };
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(
        options.queryKey,
        context?.previousNavigationCustomization
      );
    },
    // no need to handle onSettled - nothing changes on success!
  });
}
